import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { PharmaceuticalMarketAnalysisByAddressRequestModel } from '../models/licensed/pharmaceutical-market-analysis-by-address-request.model';
import { PharmaceuticalMarketAnalysisByCNPJRequestModel } from '../models/licensed/pharmaceutical-market-analysis-by-cnpj-request.model';
import { PharmaceuticalMarketAnalysisResponseModel } from '../models/licensed/pharmaceutical-market-analysis-response.model';
import { UpdateEconomicGroupUserResponseModel } from '../models/users/responses/update-economic-group-user-response.model';
import { RemoveEconomicGroupUserRequestModel } from '../models/users/requests/remove-economic-group-user-request.model';
import { CommercialConditionResponseModel } from '../models/registers/response/commercial-condition-response.model';
import { CreateCommercialCondition } from '../models/registers/request/create-commercial-condition-request.model';
import { UpdateCommercialCondition } from '../models/registers/request/update-commercial-condition-request.model';
import { SearchScoreByCPFResponseModel } from '../models/licensed/responses/search-score-by-cpf-response.model';
import { AutocompleteProductResponse } from '../models/common/response/autocomplete-product-search-response';
import { AutocompleteProductRequest } from '../models/common/requests/autocomplete-product-search-request';
import { EconomicGroupCNPJResponseModel } from '../models/licensed/economic-group-cnpj-response.model';
import { UpdateUserEmailRequestModel } from '../models/users/requests/update-user-email-request.model';
import { UpdateProductRequest } from '../../modules/products/models/requests/update-product-request';
import { CreateProductRequesModel } from '../models/products/requests/create-product-request.model';
import { UpdateProductRequesModel } from '../models/products/requests/update-product-request.model';
import { LaboratoryResponseModel } from '../models/products/response/laboratory-response.model';
import { GuestTokenResponseModel } from '../models/common/response/guest-token-response.model';
import { NecAbertoResponseModel } from '../models/products/response/nec-aberto-response.model';
import { ProductListResponseModel } from '../models/products/response/product-response.model';
import { EconomicGroupResponseModel } from '../models/licensed/economic-group-response.model';
import { CategoryResponseModel } from '../models/registers/response/category-response.model';
import { UpdateUserRequestModel } from '../models/users/requests/update-user-request.model';
import { MoleculeResponseModel } from '../models/products/response/molecule-response.model';
import { CreateUserResponseModel } from '../models/users/create-user-response.model';
import { Nec2ResponseModel } from '../models/products/response/nec-2-response.model';
import { Nec1ResponseModel } from '../models/products/response/nec1-response.model';
import { ViaCEPResponseModel } from '../models/licensed/via-cep-response.model';
import { CreateUserRequest } from '../models/users/create-user-request.model';
import { EconomicGroupModel } from '../models/licensed/economic-group.model';
import { FurnitureSupplierListResponseModel, FurnitureSupplierSingleResponseModel } from '../models/registers/response/furniture-supplier.model';
import { UsersResponseModel } from '../models/users/users-response.model';
import { environment } from '../../../environments/environment';
import { ContractResponseModel } from '../models/licensed/responses/contract-response.model';
import { ContractsRequestModel } from '../models/licensed/requests/contracts-request.model';
import { StoreResponseModel } from '../models/registers/response/StoreResponseModel';
import { StoreFilterRequestModel } from '../models/registers/request/storeFilterRequestModel';
import { StoreRequestModel } from '../models/registers/request/storeRequestModel';
import { BasicStoreData } from '../models/registers/request/storeUpdateModel';
import { UpdateEconomicGroupStoreRequestModel } from '../models/registers/request/updateEconomicGroupStore.model';
import { CnpjListResponseModel } from '../models/licensed/cnpj-list-response.model';

export interface ServerStatusResponse {
  nomeEstacao: string;
  bloqueado: boolean;
  onLine: boolean;
  dataCadasto: string;
}

export interface MidasActivationRequest {
  cnpj: string;
  nomeServidor: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient) { }

  private get getApiUrl(): string {
    return environment.apiURL;
  }

  // private get getPharmaDashApiUrl(): string {
  //   return environment.apiPharmaDash;
  // }

  private get getApiUrlViaCEP(): string {
    return environment.apiURLViaCEP;
  }

  getEconomicGroup(page: number): Observable<EconomicGroupModel> {
    return this.http.get<EconomicGroupModel>(
      `${this.getApiUrl}/economic-group?page=${page}&size=10`
    );
  }

  getEconomicGroupByName(name: string): Observable<EconomicGroupModel> {
    return this.http.get<EconomicGroupModel>(
      `${this.getApiUrl}/economic-group?economic_group_name=${name}&page=0`
    );
  }

  getAllEconomicGroup(): Observable<EconomicGroupResponseModel> {
    return this.http.get<EconomicGroupResponseModel>(
      `${this.getApiUrl}/economic-group/list`
    );
  }

  getAllCNPJ(): Observable<EconomicGroupCNPJResponseModel> {
    return this.http.get<EconomicGroupCNPJResponseModel>(
      `${this.getApiUrl}/economic-group/cnpj/list`
    );
  }

  createEconomicGroup(name: string): Observable<any> {
    return this.http.post<any>(
      `${this.getApiUrl}/economic-group`,
      { name: name }
    );
  }

  getFurnitureSuppliers(includeInactive: boolean = false): Observable<FurnitureSupplierListResponseModel> {
    return this.http.get<FurnitureSupplierListResponseModel>(
      `${this.getApiUrl}/furniture-suppliers?include_inactive=${includeInactive}`
    );
  }

  createFurnitureSupplier(name: string): Observable<FurnitureSupplierSingleResponseModel> {
    return this.http.post<FurnitureSupplierSingleResponseModel>(
      `${this.getApiUrl}/furniture-suppliers`,
      { name }
    );
  }

  renameFurnitureSupplier(id: number, name: string): Observable<FurnitureSupplierSingleResponseModel> {
    return this.http.put<FurnitureSupplierSingleResponseModel>(
      `${this.getApiUrl}/furniture-suppliers/${id}`,
      { name }
    );
  }

  changeFurnitureSupplierStatus(id: number, active: boolean): Observable<void> {
    return this.http.put<void>(
      `${this.getApiUrl}/furniture-suppliers/${id}/status`,
      { active }
    );
  }

  updateEconomicGroup(id: number, name: string, status: string): Observable<any> {
    return this.http.put<any>(
      `${this.getApiUrl}/economic-group/${id}`,
      { name: name, status: status }
    );
  }

  bulkUpdateEconomicGroupStatus(ids: number[], status: string): Observable<any> {
    return this.http.put<any>(
      `${this.getApiUrl}/economic-group/bulk-update-status`,
      { ids: ids, status: status }
    );
  }

  updateEconomicGroupStore(request: UpdateEconomicGroupStoreRequestModel): Observable<any> {
    return this.http.put<any>(
      `${this.getApiUrl}/economic-group`,
      request
    );
  }

  getUsers(page: number): Observable<UsersResponseModel> {
    return this.http.get<UsersResponseModel>(
      `${this.getApiUrl}/users?page=${page}&size=7`
    );
  }

  getUserByEmail(email: string): Observable<UsersResponseModel> {
    return this.http.get<UsersResponseModel>(
      `${this.getApiUrl}/user?email=${email}`
    );
  }

  getUserByCnpj(cnpj: string): Observable<UsersResponseModel> {
    return this.http.get<UsersResponseModel>(
      `${this.getApiUrl}/user?cnpj=${cnpj}`
    );
  }

  getUserByGroup(group: string): Observable<UsersResponseModel> {
    return this.http.get<UsersResponseModel>(
      `${this.getApiUrl}/user?grupo=${group}`
    );
  }

  createUser(request: CreateUserRequest): Observable<CreateUserResponseModel> {
    return this.http.post<CreateUserResponseModel>(
      `${this.getApiUrl}/user`,
      request
    );
  }

  updateUserEmail(request: UpdateUserEmailRequestModel): Observable<any> {
    return this.http.put<any>(`${this.getApiUrl}/user`, request);
  }

  updateEconomicGroupUser(
    request: UpdateUserRequestModel
  ): Observable<UpdateEconomicGroupUserResponseModel> {
    return this.http.post<UpdateEconomicGroupUserResponseModel>(
      `${this.getApiUrl}/economic-group-user`,
      request
    );
  }

  deleteUser(id: number): Observable<any> {
    return this.http.delete<any>(`${this.getApiUrl}/user/${id}`);
  }

  deleteEconomicGroupUser(
    request: RemoveEconomicGroupUserRequestModel
  ): Observable<any> {
    const options = {
      body: request,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      // outras opções como parâmetros de consulta podem ser incluídas aqui
    };

    return this.http.delete<any>(
      `${this.getApiUrl}/economic-group-user`,
      options
    );
  }

  performPharmaceuticalMarketAnalysis(
    request:
      | PharmaceuticalMarketAnalysisByAddressRequestModel
      | PharmaceuticalMarketAnalysisByCNPJRequestModel
  ): Observable<PharmaceuticalMarketAnalysisResponseModel> {
    return this.http.post<PharmaceuticalMarketAnalysisResponseModel>(
      `${this.getApiUrl}/market-analysis/search`,
      request
    );
  }

  getCEP(cep: string): Observable<ViaCEPResponseModel> {
    return this.http.get<ViaCEPResponseModel>(
      `${this.getApiUrlViaCEP}/ws/${cep}/json`
    );
  }

  getGuestToken(id: number): Observable<GuestTokenResponseModel> {
    return this.http.get<GuestTokenResponseModel>(
      `${this.getApiUrl}/superset/guest-token/dashboard/${id}`
    );
  }

  getProductByEAN(product: string): Observable<ProductListResponseModel> {
    return this.http.get<ProductListResponseModel>(
      `${this.getApiUrl}/product/${product}`
    );
  }

  getProductByName(product: string): Observable<ProductListResponseModel> {
    const params: HttpParams = new HttpParams().set('produto', product);

    return this.http.get<ProductListResponseModel>(
      `${this.getApiUrl}/product/search-by-text?produto`,
      { params }
    );
  }

  getLaboratory(): Observable<LaboratoryResponseModel> {
    return this.http.get<LaboratoryResponseModel>(
      `${this.getApiUrl}/product/laboratory`
    );
  }

  getMolecule(): Observable<MoleculeResponseModel> {
    return this.http.get<MoleculeResponseModel>(
      `${this.getApiUrl}/product/molecule`
    );
  }

  getNec1(): Observable<Nec1ResponseModel> {
    return this.http.get<Nec1ResponseModel>(`${this.getApiUrl}/product/nec1`);
  }

  getNec2(nec1: string): Observable<Nec2ResponseModel> {
    return this.http.get<Nec2ResponseModel>(
      `${this.getApiUrl}/product/search-by-nec?nec2=${nec1}`
    );
  }

  getNecAberto(): Observable<NecAbertoResponseModel> {
    return this.http.get<NecAbertoResponseModel>(
      `${this.getApiUrl}/product/nec-aberto`
    );
  }

  // updateProduct(request: UpdateProductRequesModel): Observable<any> {
  //   return this.http.post<any>(`${this.getApiUrl}/product`, request);
  // }

  createProduct(request: CreateProductRequesModel): Observable<any> {
    return this.http.post<any>(`${this.getApiUrl}/product`, request);
  }

  getCommercialCondition(
    page: number
  ): Observable<CommercialConditionResponseModel> {
    return this.http.get<CommercialConditionResponseModel>(
      `${this.getApiUrl}/commercial-management/condition-commercial?page=${page}&size=7`
    );
  }

  createCommercialCondition(
    request: CreateCommercialCondition
  ): Observable<any> {
    return this.http.post<any>(
      `${this.getApiUrl}/commercial-management/condition-commercial`,
      request
    );
  }

  updateCommercialCondition(
    request: UpdateCommercialCondition,
    id: number
  ): Observable<any> {
    return this.http.put<any>(
      `${this.getApiUrl}/commercial-management/condition-commercial/${id}`,
      request
    );
  }

  getCategory(): Observable<CategoryResponseModel[]> {
    return this.http.get<CategoryResponseModel[]>(
      `${this.getApiUrl}/commercial-management/category`
    );
  }

  getManufacturers(): Observable<string[]> {
    return this.http.get<string[]>(
      `${this.getApiUrl}/commercial-management/manufacturers`
    );
  }

  getDistributors(): Observable<string[]> {
    return this.http.get<any>(
      `${this.getApiUrl}/commercial-management/distributors`
    );
  }

  getScore(cpf: string): Observable<SearchScoreByCPFResponseModel> {
    return this.http.get<SearchScoreByCPFResponseModel>(
      `${this.getApiUrl}/corporate/score-cpf/${cpf}`
    );
  }

  autocompleteProductSearch(
    request: AutocompleteProductRequest
  ): Observable<AutocompleteProductResponse> {
    return this.http.post<AutocompleteProductResponse>(
      `${environment.API_URL_LEXOS}/indexes/products/search`,
      request
    );
  }

  updateProduct(request: UpdateProductRequest): Observable<any> {
    return this.http.post<any>(
      `${this.getApiUrl}/product/associations`,
      request
    );
  }

  getContracts(
    request: ContractsRequestModel
  ): Observable<ContractResponseModel> {
    return this.http.post<ContractResponseModel>(
      `${this.getApiUrl}/contracts`,
      request
    );
  }

  getStore(
    request: StoreFilterRequestModel
  ): Observable<StoreResponseModel> {
    return this.http.post<StoreResponseModel>(
      `${this.getApiUrl}/stores`,
      request
    );
  }

  getCnpjListByEconomicGroupId(economicGroupId: number): Observable<CnpjListResponseModel> {
    return this.http.get<CnpjListResponseModel>(
      `${this.getApiUrl}/store/stores-by-economic-group/${economicGroupId}`
    );
  }

  createStore(request: StoreRequestModel): Observable<StoreResponseModel> {
    return this.http.post<any>(`${this.getApiUrl}/store`, request);
  }

  updateStore(request: BasicStoreData): Observable<StoreResponseModel> {
    return this.http.put<any>(`${this.getApiUrl}/store/${request.id}`, request);
  }

  getAddressByCep(cep: string): Observable<any> {
    return this.http.get<any>(`https://viacep.com.br/ws/${cep}/json/`);
  }

  updateStoreBlock(
    storeId: number,
    data: { blocked: boolean }
  ): Observable<any> {
    return this.http.put<any>(
      `${this.getApiUrl}/store/${storeId}/block`,
      data
    );
  }

  updateStoreStage(storeId: number, data: { stage: string }): Observable<any> {
    return this.http.put<any>(`${this.getApiUrl}/store/${storeId}/stage`, data);
  }

  updateStoreCreatedAt(storeId: number, data: { created_at: string }): Observable<any> {
    return this.http.put<any>(
      `${this.getApiUrl}/store/${storeId}/created-at`,
      data
    );
  }

  registerStoreChangeUser(storeId: number, email: string): Observable<any> {
    return this.http.put<any>(
      `${this.getApiUrl}/store/${storeId}/change-user?email=${encodeURIComponent(email)}`,
      {}
    );
  }

  updateStoreAddress(
    storeId: number,
    addressId: number,
    data: any
  ): Observable<any> {
    return this.http.put<any>(
      `${this.getApiUrl}/store/${storeId}/address/${addressId}`,
      data
    );
  }

  createStoreAddress(storeId: number, data: any): Observable<any> {
    return this.http.post<any>(
      `${this.getApiUrl}/store/${storeId}/address`,
      data
    );
  }

  updateStoreContact(
    storeId: number,
    contactId: number,
    data: any
  ): Observable<any> {
    return this.http.put<any>(
      `${this.getApiUrl}/store/${storeId}/contact/${contactId}`,
      data
    );
  }

  createStoreContact(storeId: number, data: any): Observable<any> {
    return this.http.post<any>(
      `${this.getApiUrl}/store/${storeId}/contact`,
      data
    );
  }

  deleteStoreContact(storeId: number, contactId: number): Observable<any> {
    return this.http.delete<any>(
      `${this.getApiUrl}/store/${storeId}/contact/${contactId}`
    );
  }

  getEmployees(): Observable<any> {
    return this.http.get<any>(`${this.getApiUrl}/employees`);
  }

  createStoreConsultant(payload: any): Observable<any> {
    return this.http.post<any>(
      `${this.getApiUrl}/store/consultant`,
      payload);
  }

  updateStoreConsultant(payload: any): Observable<any> {
    return this.http.put<any>(
      `${this.getApiUrl}/store/consultant`,
      payload
    );
  }
  getSoftware(): Observable<any> {
    return this.http.get<any>(`${this.getApiUrl}/pharmasync/software`);
  }

  searchDocumentsByCnpj(cnpj: string, page: number = 0, size: number = 10): Observable<any> {
    const url = `${this.getApiUrl}/documents/search`;
    const cleanCnpj = cnpj.replace(/[^0-9]/g, '');
    const body = {
      cnpj: cleanCnpj,
      active: true,
      page: page,
      size: size
    };
    return this.http.post<any>(url, body);
  }

  updateDocumentsStatus(documents: { id: number, active: boolean }[]): Observable<any> {
    const body = {
      documents: documents
    };
    return this.http.put<any>(`${this.getApiUrl}/documents/status`, body);
  }

  uploadDocument(cnpj: string, title: string, fileName: string, path: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('cnpj', cnpj);
    formData.append('title', title);
    formData.append('fileName', fileName);
    formData.append('path', path);
    formData.append('docId', '0');
    formData.append('file', file);

    return this.http.post<any>(`${this.getApiUrl}/documents/upload`, formData);
  }

  updateDocument(docId: string, cnpj: string, title: string, fileName: string, path: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('cnpj', cnpj);
    formData.append('title', title);
    formData.append('fileName', fileName);
    formData.append('path', path);
    formData.append('docId', docId);
    formData.append('file', file);

    return this.http.post<any>(`${this.getApiUrl}/documents/upload`, formData);
  }

  getInovaServerInfo(cnpj: string): Observable<ServerStatusResponse[]> {
    const url = environment.inovaServerCheckUrl;
    return this.http.post<ServerStatusResponse[]>(url, { cnpj });
  }

  activateInovaPricing(cnpj: string, nomeServidor: string): Observable<any> {
    const url = environment.inovaActivationUrl;
    const body: MidasActivationRequest = { cnpj, nomeServidor };
    return this.http.post(url, body);
  }
}
